import { useEffect, useMemo, useRef, useState } from 'react';

import { message } from 'antd';
import type { TInfodata } from 'types/infodata';
import type { TMetadata } from 'types/metadata';

import { CRMAPIManager } from '@classes/crmApiManager';
import { GlobalConstants } from '@constants/global';
import { useInfiniteQuery } from '@tanstack/react-query';

import { stableStringify } from './stableStringify';
import { ExtractInnerData, GetFnKey, ParamsMap, ResponseMap } from './types';

type TMetaAdditional = TMetadata & TInfodata;

interface IProps<K extends GetFnKey> {
    getFnKey: K;
    params?: ParamsMap[K];
    initialPage?: number;
    setDataList?: (data: ExtractInnerData<ResponseMap[K]>) => void;
    setRootDataList?: (data: ResponseMap[K]) => void;
    pageSize?: number;
    queryMeta?: TMetaAdditional;
    setQueryMeta?: (meta: TMetaAdditional) => void;
    forceReloadState?: number;
    enabled?: boolean;
}

function isArrayParams<K extends GetFnKey>(
    params: ParamsMap[K]
): params is ParamsMap[K] & unknown[] {
    return Array.isArray(params);
}

export const useFetchDataQuery = <K extends GetFnKey>({
    getFnKey,
    initialPage,
    setDataList,
    setRootDataList,
    params,
    pageSize,
    queryMeta,
    setQueryMeta,
    forceReloadState,
    enabled = true,
}: IProps<K>) => {
    const getParams = (page: number): ParamsMap[K] => ({
        page,
        per_page: pageSize ?? GlobalConstants.ElementsPerPage,
        ...params,
    });

    const [visiblePages, setVisiblePages] = useState<any[]>([]);

    const {
        data,
        error,
        isError,
        isFetching,
        isLoading,
        isSuccess,
        fetchNextPage,
        fetchPreviousPage,
        hasNextPage,
        refetch,
        isRefetching,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isPending,
    } = useInfiniteQuery({
        queryKey: [getFnKey, stableStringify(params), forceReloadState],

        initialPageParam: initialPage ?? 1,

        queryFn: async ({ pageParam = initialPage ?? 1 }) => {
            const newParams = getParams(pageParam);
            const result = await CRMAPIManager.request<ResponseMap[K]>((api) => {
                if (isArrayParams(params)) {
                    return api[getFnKey](...params);
                } else if (typeof params === 'object' && params !== null) {
                    return (api[getFnKey] as (arg: ParamsMap[K]) => any)(newParams);
                } else {
                    return (api[getFnKey] as (arg: ParamsMap[K]) => any)(params);
                }
            });

            if (result.statusCode === 404 || result.statusCode === 403) throw result;

            return {
                ...result,
                __page: pageParam,
            };
        },

        refetchOnMount: 'always',
        staleTime: 1000 * 60 * 5,
        getNextPageParam: (lastPage: any) => {
            const meta = lastPage?.data?.meta;
            const currentPage = meta?.current_page ?? 1;
            const totalPages = meta?.last_page ?? 1;
            return currentPage < totalPages ? currentPage + 1 : undefined;
        },
        getPreviousPageParam: (firstPage: any) => {
            const meta = firstPage?.data?.meta;
            const currentPage = meta?.current_page ?? 1;
            return currentPage > 1 ? currentPage - 1 : undefined;
        },
        enabled,
        retry: (failureCount, error: any) => {
            if (error?.statusCode === 404 || error?.statusCode === 403) return false;
            return failureCount < 3;
        },
    });

    useEffect(() => {
        if (!data?.pages?.length || initialShown.current) return;
        const initial = data.pages.find((p) => p.__page === initialPage) ?? data.pages[0];
        setVisiblePages([initial]);
        initialShown.current = true;
    }, [data?.pages]);

    const appendNextPage = (page: any) => {
        setVisiblePages((prev) => {
            if (prev.some((p) => p.__page === page.__page)) return prev;
            return [...prev, page];
        });
    };

    const prependPrevPage = (page: any) => {
        setVisiblePages((prev) => {
            if (prev.some((p) => p.__page === page.__page)) return prev;
            return [page, ...prev];
        });
    };

    const loadNextPage = async () => {
        const nextPage = await fetchNextPage();

        const rightmostPageNumber = Math.max(...visiblePages.map((p) => p.__page ?? -Infinity));

        const pageToAppend = nextPage.data.pages.find((p) => p.__page === rightmostPageNumber + 1);

        if (pageToAppend) {
            appendNextPage(pageToAppend);
        }
    };

    const loadPrevPage = async () => {
        const prevPageResult = await fetchPreviousPage();
        if (!prevPageResult?.data?.pages?.length) return;

        const leftmostPageNumber = Math.min(...visiblePages.map((p) => p.__page ?? Infinity));
        const pageToPrepend = prevPageResult.data.pages.find(
            (p) => p.__page === leftmostPageNumber - 1
        );

        if (pageToPrepend) {
            prependPrevPage(pageToPrepend);
        }
    };

    const initialShown = useRef(initialPage ? false : true);

    const resetInitialShown = () => {
        initialShown.current = false;
    };

    const flatData = useMemo(() => {
        const pages = initialPage ? visiblePages : data?.pages; // фикс для старой логики запросов

        if (!pages?.length) return null;

        const sortedPages = [...pages].sort((a, b) => (a.__page ?? 0) - (b.__page ?? 0));

        const allPayloads = sortedPages
            .map((page) => page?.data?.data ?? page?.data)
            .filter(Boolean);

        const isArrayPayload = Array.isArray(allPayloads[0]);
        return isArrayPayload ? allPayloads.flat() : allPayloads[0];
    }, [visiblePages, initialPage, data]);

    const rootData: ResponseMap[K] = useMemo(() => {
        if (!data || !isSuccess) return null;

        const allRoots = data.pages.map((page) => page?.data).filter(Boolean);
        const isArrayPayload = Array.isArray(allRoots[0]);
        return isArrayPayload ? allRoots.flat() : allRoots[0];
    }, [data, isSuccess]);

    useEffect(() => {
        if (isError && error) {
            const errorMessage = error.message || 'Произошла ошибка при загрузке данных';
            message.error(errorMessage);
        }
    }, [isError, error]);

    useEffect(() => {
        if (flatData) {
            setDataList?.(flatData);

            const lastMeta = data?.pages?.[data.pages.length - 1]?.data?.meta;
            if (lastMeta && Array.isArray(flatData)) {
                setQueryMeta?.({
                    ...(queryMeta ?? {}),
                    ...lastMeta,
                });
            }
        }
    }, [flatData]);

    useEffect(() => {
        if (rootData) {
            setRootDataList?.(rootData);
        }
    }, [rootData]);

    return {
        data: flatData,
        pages: visiblePages,
        rootData,
        isLoading,
        isFetching,
        isSuccess,
        isError,
        refetch,
        fetchNextPage: loadNextPage,
        fetchPreviousPage: loadPrevPage,
        resetInitialShown,
        hasNextPage,
        isRefetching,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isPending,
        error,
    };
};
